import { RightOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import posthog from '@leyan/lytics';
import { Button, Tag as ATag } from 'antd';
import { memo, useCallback, useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import { useScenarioContext } from './ScenarioContext';
import { useTransAppProductName } from 'hooks/useTrans';
import { useQAPairConversationKnowledgeQuery, useQAPairVisible } from 'hooks/business/im/useQAPair';
import { isIdEqual } from 'utils/is';
import { useGetPermission } from 'hooks/business/useNewPermisson';
import { fromBtn } from '../MessageExtend/styles';
import { LLMQAReplyAction } from './LLMQAReplyAction';

type OperationProps = ScenarioPreviewProps & {
  extraInfo?: Conversation.ExtraInfo;
  botReplyType: Conversation.BotReplyType;
  scenarioText: string;
};

const notSupportOperationTypes: Conversation.BotReplyType[] = [
  'TO_BUYER_AUTO_REPLY',
  'COMPENSATORY_REPLY',
];

export interface ScenarioPreviewProps {
  conversation: Conversation.Conversation;
  message: Conversation.Message;
  galaxyModule?: Conversation.GalaxyModule;
  isGalaxy?: boolean;
}

const getLLMAgentText = (extraInfo: any) => {
  return extraInfo?.topicOutput?.topic as string | undefined;
};

const DefaultText = memo(() => {
  const appProductNameT = useTransAppProductName();

  return (
    <Container>
      <Tag>{appProductNameT('ViewsChat.aiReplyOnClient')}</Tag>
    </Container>
  );
});

const LLMAgentText = memo((props: { extraInfo?: Conversation.ExtraInfo }) => {
  const { extraInfo } = props;
  const topic = getLLMAgentText(extraInfo);

  if (!topic) {
    return <DefaultText />;
  }

  return (
    <Container>
      <Tag>{topic}</Tag>
    </Container>
  );
});

const DialogueLLMOperation = memo((props: OperationProps) => {
  const { message, extraInfo, conversation, botReplyType } = props;

  const { t } = useTranslate();
  const conversationKnowledgeQuery = useQAPairConversationKnowledgeQuery();

  const {
    modal: { show },
  } = useScenarioContext();

  if (!extraInfo || !extraInfo?.spuId) {
    return null;
  }

  const reqMsgId = extraInfo?.reqMsgId;
  const dialogueReplyType = extraInfo?.dialogueReplyType;

  if (
    extraInfo &&
    reqMsgId &&
    (dialogueReplyType === 'DRT_LLM_QA_REPLY' || dialogueReplyType === 'DRT_REPLY_AND_H2H')
  ) {
    if (
      conversationKnowledgeQuery &&
      isIdEqual(conversationKnowledgeQuery.conversationId, conversation.conversationId) &&
      conversationKnowledgeQuery.knowledgeReplies[reqMsgId]
    ) {
      return (
        <LLMQAReplyAction
          message={message}
          conversation={conversation}
          extraInfo={extraInfo}
          knowledgeQuery={conversationKnowledgeQuery.knowledgeReplies[reqMsgId].query}
        />
      );
    }
  }

  if (message?.status === 'SUCCESS') {
    return (
      <Button
        type="link"
        className={fromBtn}
        onClick={() => {
          show({ message, botReplyType, extraInfo, spuId: extraInfo.spuId });
          posthog.capture('$previewScenario', {
            botReplyType,
            extraInfo,
          });
        }}
      >
        {t('scene.chatWorkspace.messageScenario.action.adjustAnswer')}
        <RightOutlined />
      </Button>
    );
  }

  return null;
});

const Operation = memo((props: OperationProps) => {
  const { message, extraInfo, botReplyType, isGalaxy, scenarioText } = props;
  const { t } = useTranslate();

  const {
    modal: { show },
  } = useScenarioContext();
  const getPermission = useGetPermission();

  const visible = useMemo(() => {
    if (
      !extraInfo ||
      notSupportOperationTypes.includes(botReplyType) ||
      isGalaxy ||
      extraInfo?.defaultTemplate
    ) {
      return false;
    }

    return true;
  }, [botReplyType, extraInfo, isGalaxy]);

  const handleClick = useCallback(() => {
    if (!extraInfo) {
      return;
    }
    show({ message, botReplyType, extraInfo });
    posthog.capture('$previewScenario', {
      botReplyType,
      extraInfo,
    });
  }, [message, botReplyType, extraInfo, show]);

  if (!visible || !extraInfo) {
    return null;
  }

  const { botDialogueReplyType, spuId, llmbasicInfo } = extraInfo;

  if (botReplyType === 'LLM_AGENT') {
    if (!!llmbasicInfo?.llmIntentId) {
      return (
        <Button type="link" className={fromBtn} onClick={handleClick}>
          {t('调整配置')}
          <RightOutlined />
        </Button>
      );
    }

    if (botDialogueReplyType === 'BDRT_LLM' && !!spuId && getPermission('AI智能接待')) {
      // 大模型 QAPair 场景
      return <DialogueLLMOperation {...props} />;
    }
    return null;
  }

  if (botReplyType === 'DIALOGUE') {
    if (botDialogueReplyType === 'BDRT_RULE') {
      // 小模型
      return (
        <Button type="link" className={fromBtn} onClick={handleClick}>
          {t('scene.chatWorkspace.messageScenario.action.adjustAnswer')}
          <RightOutlined />
        </Button>
      );
    }

    if (botDialogueReplyType === 'BDRT_LLM' && !!spuId && getPermission('AI智能接待')) {
      // 大模型 QAPair 场景
      return <DialogueLLMOperation {...props} />;
    }
    return null;
  }

  if (scenarioText) {
    return (
      <Button type="link" className={fromBtn} onClick={handleClick}>
        {t('scene.chatWorkspace.messageScenario.action.adjustScenarioName', {
          scenario: scenarioText,
        })}
        <RightOutlined />
      </Button>
    );
  }

  return null;
});

function ScenarioPreview(props: ScenarioPreviewProps) {
  const { message, galaxyModule } = props;

  const { botReplyType, extra: extraInfoJson, extraInfo: parsedExtraInfo } = message;

  const { t } = useTranslate();
  const qaPairVisible = useQAPairVisible();

  const extraInfo = useMemo(() => {
    if (parsedExtraInfo) {
      return parsedExtraInfo;
    }

    if (!extraInfoJson) {
      return undefined;
    }

    try {
      const extraInfo: Conversation.ExtraInfo = JSON.parse(extraInfoJson);

      return extraInfo;
    } catch (err) {
      return undefined;
    }
  }, [extraInfoJson, parsedExtraInfo]);

  const enrichedExtraInfo = useMemo(() => {
    if (!extraInfo?.llmbasicInfo) return extraInfo;
    return { ...extraInfo, ...extraInfo.llmbasicInfo };
  }, [extraInfo]);

  const { getScenarioText } = useScenarioContext();

  if (!botReplyType) {
    return null;
  }

  // 增强模型
  if (botReplyType === 'LLM_AGENT' && galaxyModule === 'aiReplyReport') {
    return <LLMAgentText extraInfo={enrichedExtraInfo} />;
  }

  const scenarioText = getScenarioText(botReplyType, enrichedExtraInfo);

  if (!scenarioText) {
    return <DefaultText />;
  }

  return (
    <Container>
      <Tag>{scenarioText}</Tag>
      {enrichedExtraInfo?.dialogueReplyType === 'DRT_REPLY_AND_H2H' && (
        <ATag color="volcano" style={{ margin: '0 0 0 8px' }}>
          {t('scene.chatWorkspace.aiReception.status.transferredToAgent')}
        </ATag>
      )}
      {!qaPairVisible && (
        <>
          <Operation
            {...props}
            extraInfo={enrichedExtraInfo}
            botReplyType={botReplyType}
            scenarioText={scenarioText}
          />
        </>
      )}
    </Container>
  );
}

export default memo(ScenarioPreview);

const Container = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  margin: 4px 0;
  font-size: 12px;
  > button {
    padding: 0;
    padding-left: 4px;
    font-size: 12px;
    .anticon {
      margin-left: 2px;
    }
  }
`;

export const Tag = styled.div`
  height: 24px;
  line-height: 24px;
  padding: 0 8px;
  color: var(--fett-scenario-success-text);
  background: var(--fett-surface-success-bg);
  border-radius: 2px;
  /* dark 走 UED Tag Green 填充浅色型；light 保持场景成功色不变 */
  [data-color-mode='dark'] & {
    color: var(--fett-tag-green-text);
    background: var(--fett-tag-green-bg);
  }
`;
